import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";

import AppsIcon from "@mui/icons-material/Apps";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import BusinessIcon from "@mui/icons-material/Business";

import { DRAWER_WIDTH } from "./Header";

/**
 * Sidebar navigation — MUI Drawer.
 *
 * - Desktop (md+): permanent, always on the left at DRAWER_WIDTH. The
 *   Header hamburger toggles it to temporary+hidden so users can reclaim
 *   screen width.
 * - Mobile (< md): temporary; tap hamburger in Header to open.
 *
 * Visibility is persisted in localStorage under `sidebarVisible` so
 * Header + Sidebar stay in sync, and multi-tab sync works via the
 * `storage` event.
 */
const Sidebar = () => {
  const location = useLocation();
  const organization = useSelector((s) => s.organizationDetails.organization);
  const {
    userLogin: { userInfo: { data } },
  } = useSelector((s) => s);

  const isDesktop = useMediaQuery((t) => t.breakpoints.up("md"));

  // On mobile the Drawer is closed by default; on desktop, open by default.
  // When the user has explicitly toggled it, respect that choice.
  const getInitial = () => {
    const stored = localStorage.getItem("sidebarVisible");
    if (stored === "true") return true;
    if (stored === "false") return false;
    return isDesktop; // default: visible on desktop, hidden on mobile
  };
  const [open, setOpen] = useState(getInitial);

  // Sync across tabs and with Header toggles
  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem("sidebarVisible");
      if (stored === "true") setOpen(true);
      else if (stored === "false") setOpen(false);
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // When breakpoint flips (rotate / resize), reset to the breakpoint's default
  useEffect(() => {
    const stored = localStorage.getItem("sidebarVisible");
    if (stored === null) setOpen(isDesktop);
  }, [isDesktop]);

  const close = () => {
    setOpen(false);
    localStorage.setItem("sidebarVisible", "false");
    window.dispatchEvent(new Event("storage"));
  };

  const organizationuserRole = organization?.user_role;
  const isCreateOrgRoute = location.pathname === "/organization/create";
  const isSuperAdmin = data?.role === "Super_Admin";
  const isOrgAdmin = organizationuserRole === "Admin";

  const matchesPath = (paths) => paths.some((p) => location.pathname.includes(p));
  const activeApps = matchesPath(["/apps", "/app"]);
  const activeUsers = matchesPath(["/users", "/user"]);
  const activeOrgs = matchesPath(["/organizations", "/organization"]);

  // Build menu entries based on role + route (preserves legacy logic)
  const items = [];
  if (!isSuperAdmin && isOrgAdmin && !isCreateOrgRoute) {
    items.push({ label: "Apps", to: "/apps", icon: <AppsIcon />, active: activeApps });
    items.push({ label: "Users", to: "/users", icon: <PeopleAltOutlinedIcon />, active: activeUsers });
  } else if (!isSuperAdmin && !isOrgAdmin && !isCreateOrgRoute) {
    items.push({ label: "Apps", to: "/apps", icon: <AppsIcon />, active: activeApps });
  }

  if (isCreateOrgRoute) {
    items.push({ label: "Organization", to: "#", icon: <BusinessIcon />, active: true });
  }

  if (isSuperAdmin) {
    items.push({ label: "Organizations", to: "/organizations", icon: <CorporateFareIcon />, active: activeOrgs });
  }

  const content = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        pt: 2,
      }}
    >
      {/* Under the fixed AppBar — spacer matches Toolbar height so first
          item isn't occluded. */}
      <Toolbar disableGutters sx={{ minHeight: { xs: 0, md: 0 } }} />

      <List sx={{ px: 1.5, flexGrow: 1 }}>
        {items.map((item) => (
          <ListItemButton
            key={item.label}
            component={RouterLink}
            to={item.to}
            selected={item.active}
            onClick={!isDesktop ? close : undefined}
            sx={{
              borderRadius: 2,
              my: 0.5,
              color: "text.primary",
              "&.Mui-selected": {
                backgroundColor: "primary.light",
                color: "secondary.dark",
                fontWeight: 700,
                "& .MuiListItemIcon-root": { color: "primary.dark" },
                "&:hover": { backgroundColor: "primary.light" },
              },
              "& .MuiListItemIcon-root": {
                minWidth: 36,
                color: "text.secondary",
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: item.active ? 700 : 600,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isDesktop ? "persistent" : "temporary"}
      open={open}
      onClose={close}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: open && isDesktop ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          // Sit below the fixed AppBar on desktop
          top: { md: 64 },
          height: { md: "calc(100% - 64px)" },
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default Sidebar;
